import { useState, useEffect } from "react";
import { X, Plus, Minus, Trash2 } from "lucide-react";
import { useCart } from "@/store/cart";
import { useAds } from "@/store/ads";
import { api, resolveImage } from "@/lib/api";
import { AnimatePresence, motion } from "framer-motion";

export default function CartDrawer() {
  const { items, subtotal, updateQty, removeItem, drawerOpen, setDrawerOpen, cartId } = useCart();
  const { promoCode, clearPromo, attribution } = useAds();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState(null);

  // Auto-apply promo code from ad landing (?promo=CHERRY10)
  useEffect(() => {
    if (!promoCode || appliedCoupon || items.length === 0) return;
    (async () => {
      try {
        const { data } = await api.get(`/coupons/validate/${promoCode}`);
        if (subtotal >= data.min_order) {
          setAppliedCoupon(data);
        }
      } catch {
        clearPromo();
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promoCode, items.length]);

  const applyCoupon = async () => {
    setCouponError(null);
    if (!couponCode.trim()) return;
    try {
      const { data } = await api.get(`/coupons/validate/${couponCode.trim().toUpperCase()}`);
      if (subtotal < data.min_order) {
        setCouponError(`Add $${(data.min_order - subtotal).toFixed(2)} more to use this code.`);
        return;
      }
      setAppliedCoupon(data);
    } catch (e) {
      setCouponError(e?.response?.data?.detail || "Invalid coupon");
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponCode("");
    setCouponError(null);
  };

  const discount = appliedCoupon ? subtotal * (appliedCoupon.percent_off / 100) : 0;
  const total = subtotal - discount;

  const checkout = async () => {
    setError(null);
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
        coupon_code: appliedCoupon?.code,
        cart_id: cartId,
        attribution: attribution || undefined,
      }, { withCredentials: true });
      window.location.href = data.checkout_url;
    } catch (e) {
      setError(e?.response?.data?.detail || "Checkout failed. Try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setDrawerOpen(false)}
            className="fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 240 }}
            className="fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[440px] bg-[#0a0a0a] border-l border-white/10 flex flex-col"
            data-testid="cart-drawer"
          >
            <header className="flex items-center justify-between px-6 py-5 border-b border-white/10">
              <div className="font-display text-2xl text-white">Your Bag</div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="text-white/70 hover:text-white"
                data-testid="cart-close-btn"
              >
                <X size={22} />
              </button>
            </header>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 && (
                <div className="text-white/50 text-sm text-center mt-16">
                  Your bag is empty. Go pick a vibe.
                </div>
              )}
              <ul className="space-y-4">
                {items.map((item) => (
                  <li
                    key={item.product_id}
                    className="flex gap-3 p-3 rounded-lg border border-white/10"
                    data-testid={`cart-item-${item.product_id}`}
                  >
                    <img
                      src={resolveImage(item.image, 200)}
                      alt={item.name}
                      className="w-16 h-16 object-cover rounded-md bg-white/5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-white text-sm font-semibold truncate">{item.name}</div>
                      <div className="text-[#FF3D8B] text-sm font-bold mt-0.5">
                        ${(item.price * item.quantity).toFixed(2)}
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => updateQty(item.product_id, item.quantity - 1)}
                          className="w-7 h-7 rounded-full border border-white/20 text-white/80 flex items-center justify-center hover:border-[#FF3D8B]"
                          data-testid={`cart-decrease-${item.product_id}`}
                        >
                          <Minus size={14} />
                        </button>
                        <span className="text-white text-sm w-6 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQty(item.product_id, item.quantity + 1)}
                          className="w-7 h-7 rounded-full border border-white/20 text-white/80 flex items-center justify-center hover:border-[#FF3D8B]"
                          data-testid={`cart-increase-${item.product_id}`}
                        >
                          <Plus size={14} />
                        </button>
                        <button
                          onClick={() => removeItem(item.product_id)}
                          className="ml-auto text-white/40 hover:text-[#FF3D8B]"
                          data-testid={`cart-remove-${item.product_id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <footer className="border-t border-white/10 px-6 py-5 space-y-3">
              {/* Coupon */}
              {items.length > 0 && (
                <div className="pb-3 border-b border-white/5">
                  {appliedCoupon ? (
                    <div
                      className="flex items-center justify-between text-xs"
                      data-testid="cart-coupon-applied"
                    >
                      <span className="text-[#FF3D8B]">
                        {appliedCoupon.code} — {appliedCoupon.percent_off}% off
                      </span>
                      <button
                        onClick={removeCoupon}
                        className="text-white/40 hover:text-white uppercase tracking-widest"
                        data-testid="cart-coupon-remove"
                      >
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value)}
                        placeholder="Promo code"
                        data-testid="cart-coupon-input"
                        className="flex-1 bg-white/5 border border-white/10 rounded-full px-3 py-2 text-xs text-white placeholder:text-white/40 focus:outline-none focus:border-[#FF3D8B]"
                      />
                      <button
                        onClick={applyCoupon}
                        data-testid="cart-coupon-apply"
                        className="px-4 py-2 rounded-full border border-white/20 text-white/80 text-[11px] uppercase tracking-widest hover:border-[#FF3D8B] hover:text-[#FF3D8B] transition-colors"
                      >
                        Apply
                      </button>
                    </div>
                  )}
                  {couponError && (
                    <div className="text-red-400 text-[11px] mt-1.5" data-testid="cart-coupon-error">
                      {couponError}
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-between text-white/70 text-sm">
                <span>Subtotal</span>
                <span className="text-white/90" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
              </div>
              {appliedCoupon && (
                <div className="flex justify-between text-[#FF3D8B] text-sm">
                  <span>Discount ({appliedCoupon.percent_off}%)</span>
                  <span data-testid="cart-discount">−${discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-white text-base font-semibold pt-1">
                <span>Total</span>
                <span data-testid="cart-total">${total.toFixed(2)}</span>
              </div>
              <div className="text-xs text-white/40">
                Taxes & shipping calculated at checkout.
              </div>
              {error && (
                <div className="text-xs text-red-400" data-testid="cart-error">
                  {error}
                </div>
              )}
              <button
                disabled={items.length === 0 || loading}
                onClick={checkout}
                data-testid="checkout-btn"
                className="w-full py-4 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#ff5ba0] disabled:opacity-40 disabled:cursor-not-allowed transition-colors glow-pink"
              >
                {loading ? "Redirecting…" : "Checkout"}
              </button>
            </footer>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
